import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import toast from 'react-hot-toast';
import { FaArrowLeft, FaShoppingCart, FaPlus, FaMinus, FaEye, FaStar, FaTruck, FaShieldAlt, FaCheckCircle } from 'react-icons/fa';
import { addToCustomerCart } from '../utils/cartUtils';

export default function ProductDetails() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    fetchProductDetails();
  }, [productId, fetchProductDetails]);

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const [productData, allProducts] = await Promise.all([
        apiService.getProduct(productId),
        apiService.getProducts()
      ]);

      setProduct(productData);

      // Get related products (same category, excluding current product)
      const related = allProducts
        .filter(p => p.category === productData.category && p.id !== parseInt(productId))
        .slice(0, 4);
      setRelatedProducts(related);

      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }

    try {
      await apiService.addToCart(product.id, quantity);
      toast.success(`${product.name} added to cart`);
    } catch (error) {
      console.error('Add to cart error:', error);
      toast.error('Failed to add to cart');
    }
  };

  const updateQuantity = (newQuantity) => {
    if (newQuantity < 1) return;
    setQuantity(newQuantity);
  };

  const calculateGST = () => {
    if (!product) return 0;
    return (product.sellingPrice * product.gstPercent) / 100;
  };

  const getTotalPrice = () => {
    if (!product) return 0;
    return product.sellingPrice + calculateGST();
  };

  const getRatingStars = (rating = 4.5) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} className="text-yellow-400" />);
    }

    if (hasHalfStar) {
      stars.push(<FaStar key="half" className="text-yellow-400 opacity-50" />);
    }

    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FaStar key={`empty-${i}`} className="text-gray-300" />);
    }

    return stars;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Product not found</h3>
          <p className="text-gray-500 mb-6">{error || 'The product you are looking for does not exist.'}</p>
          <Button
            onClick={() => navigate('/customer/orders')}
            variant="outline"
            className="w-full justify-center"
          >
            <FaArrowLeft className="mr-2" />
            Back to Products
          </Button>
        </div>
      </div>
    );
  }

  // Mock product images (in real app, these would come from the API)
  const productImages = [
    { id: 1, url: 'https://via.placeholder.com/400x400/4F46E5/FFFFFF?text=Product+Image+1' },
    { id: 2, url: 'https://via.placeholder.com/400x400/7C3AED/FFFFFF?text=Product+Image+2' },
    { id: 3, url: 'https://via.placeholder.com/400x400/EC4899/FFFFFF?text=Product+Image+3' },
    { id: 4, url: 'https://via.placeholder.com/400x400/10B981/FFFFFF?text=Product+Image+4' }
  ];

  return (
    <div className="w-full min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/customer/orders')}
            variant="ghost"
            className="mb-4 text-gray-600 hover:text-gray-900 pl-0"
          >
            <FaArrowLeft className="mr-2" />
            Back to Products
          </Button>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
              <p className="text-gray-600 mt-2">SKU: {product.sku}</p>
            </div>
            <div className="flex flex-col items-end">
              <div className="flex items-center space-x-1 mb-2">
                {getRatingStars()}
                <span className="text-gray-600 text-sm ml-2">4.5 (128 reviews)</span>
              </div>
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                In Stock
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="relative">
                <img
                  src={productImages[selectedImage].url}
                  alt={product.name}
                  className="w-full h-96 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => setShowImageModal(true)}
                />
                <div className="absolute top-4 right-4">
                  <Button
                    onClick={() => setShowImageModal(true)}
                    className="bg-white/80 hover:bg-white text-gray-900 border-0 shadow-sm"
                  >
                    <FaEye />
                  </Button>
                </div>
              </div>

              {/* Thumbnail Images */}
              <div className="flex space-x-4 mt-4 overflow-x-auto pb-2">
                {productImages.map((image, index) => (
                  <img
                    key={image.id}
                    src={image.url}
                    alt={`${product.name} ${index + 1}`}
                    className={`w-20 h-20 object-cover rounded-lg cursor-pointer border-2 transition-all ${selectedImage === index
                        ? 'border-blue-500 scale-105'
                        : 'border-gray-200 hover:border-blue-300'
                      }`}
                    onClick={() => setSelectedImage(index)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Description</h2>
              <p className="text-gray-600 mb-6">{product.description || 'No description available.'}</p>

              <div className="space-y-3 border-t border-gray-100 pt-4">
                <div className="flex justify-between">
                  <span className="text-gray-500">Category:</span>
                  <span className="text-gray-900 font-medium">{product.category || 'General'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">SKU:</span>
                  <span className="text-gray-900 font-medium">{product.sku}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Availability:</span>
                  <span className="text-green-600 font-medium">In Stock</span>
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Pricing</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Base Price:</span>
                  <span className="text-gray-900">₹{product.sellingPrice?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">GST ({product.gstPercent || 18}%):</span>
                  <span className="text-gray-900">₹{calculateGST().toLocaleString()}</span>
                </div>
                <div className="border-t border-gray-100 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-900 font-bold text-lg">Total Price:</span>
                    <span className="text-blue-600 font-bold text-2xl">₹{getTotalPrice().toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Add to Cart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Add to Cart</h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Quantity:</span>
                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={() => updateQuantity(quantity - 1)}
                      variant="outline"
                      className="w-10 h-10 p-0 flex items-center justify-center"
                    >
                      <FaMinus className="w-3 h-3" />
                    </Button>
                    <span className="text-gray-900 font-bold text-xl w-12 text-center">{quantity}</span>
                    <Button
                      onClick={() => updateQuantity(quantity + 1)}
                      variant="outline"
                      className="w-10 h-10 p-0 flex items-center justify-center"
                    >
                      <FaPlus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                <div className="flex justify-between items-center border-t border-gray-100 pt-4">
                  <span className="text-gray-500">Subtotal:</span>
                  <span className="text-gray-900 font-bold text-xl">₹{(getTotalPrice() * quantity).toLocaleString()}</span>
                </div>

                <Button
                  onClick={addToCart}
                  className="w-full py-3 text-lg font-semibold shadow-md hover:shadow-lg transition-shadow"
                >
                  <FaShoppingCart className="mr-2" />
                  Add to Cart
                </Button>
              </div>
            </div>

            {/* Product Specifications */}
            {product.specifications && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Specifications</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                    {typeof product.specifications === 'string'
                      ? product.specifications
                      : JSON.stringify(product.specifications, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Packaging Options */}
            {product.packagingOptions && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Packaging Options</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                    {typeof product.packagingOptions === 'string'
                      ? product.packagingOptions
                      : JSON.stringify(product.packagingOptions, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Industry Features */}
            {product.industryFeatures && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Industry Features</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                    {typeof product.industryFeatures === 'string'
                      ? product.industryFeatures
                      : JSON.stringify(product.industryFeatures, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Discount Eligibility */}
            {product.discountEligibility && (
              <div className="bg-green-50 rounded-xl border border-green-100 p-4 flex items-center gap-3">
                <FaCheckCircle className="text-green-500 text-xl" />
                <span className="text-green-700 font-medium">This product is eligible for bulk discounts</span>
              </div>
            )}
          </div>
        </div>

        {/* Product Features */}
        <div className="mb-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Buy From Us?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FaTruck className="text-2xl text-blue-600" />
                </div>
                <div>
                  <h4 className="text-gray-900 font-semibold mb-1">Free Shipping</h4>
                  <p className="text-gray-500 text-sm">On orders above ₹50,000. Fast and secure delivery to your doorstep.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <FaShieldAlt className="text-2xl text-purple-600" />
                </div>
                <div>
                  <h4 className="text-gray-900 font-semibold mb-1">Quality Assured</h4>
                  <p className="text-gray-500 text-sm">ISO certified products with rigorous quality checks.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <FaCheckCircle className="text-2xl text-green-600" />
                </div>
                <div>
                  <h4 className="text-gray-900 font-semibold mb-1">Easy Returns</h4>
                  <p className="text-gray-500 text-sm">Hassle-free 30-day return policy for damaged goods.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Products</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map(relatedProduct => (
                <div
                  key={relatedProduct.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-all duration-300 cursor-pointer group"
                  onClick={() => navigate(`/customer/product/${relatedProduct.id}`)}
                >
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-3 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <FaEye className="text-blue-600" />
                    </div>
                    <h4 className="text-gray-900 font-semibold text-sm mb-2 line-clamp-1">{relatedProduct.name}</h4>
                    <p className="text-gray-500 text-xs mb-2">{relatedProduct.sku}</p>
                    <p className="text-blue-600 font-bold">₹{relatedProduct.sellingPrice?.toLocaleString() || 0}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Image Modal */}
        <Modal isOpen={showImageModal} onClose={() => setShowImageModal(false)} title="" size="xl">
          <div className="text-center">
            <img
              src={productImages[selectedImage].url}
              alt={product.name}
              className="w-full max-h-96 object-contain rounded-lg"
            />
            <div className="flex justify-center space-x-4 mt-4">
              {productImages.map((image, index) => (
                <img
                  key={image.id}
                  src={image.url}
                  alt={`${product.name} ${index + 1}`}
                  className={`w-16 h-16 object-cover rounded-lg cursor-pointer border-2 transition-all ${selectedImage === index
                      ? 'border-blue-500'
                      : 'border-gray-200 hover:border-blue-300'
                    }`}
                  onClick={() => setSelectedImage(index)}
                />
              ))}
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}